import { defineStore } from "pinia";
import { ref } from "vue";

import { apiClient } from "@/client";
import type { CatalogUpdatePreview, Product } from "@/type/api";

/**
 * ProductPatch is one district product edit: only the fields present are
 * written, so an auto-save that changes one fact never clobbers the others.
 * `null` clears a field (the API treats null/"" as clear for life stages and
 * null as clear for the residual); omitting it leaves the stored value alone.
 */
export interface ProductPatch {
	control_class?: string | null;
	residual_days?: number | null;
	target_life_stages?: string | null;
}

export const useStoreProduct = defineStore("product", () => {
	// State
	const products = ref<Product[]>([]);
	const loading = ref(false);
	const error = ref<string | null>(null);

	// Actions
	async function list(options?: {
		catalogUpdate?: boolean;
	}): Promise<Product[]> {
		loading.value = true;
		error.value = null;
		try {
			const page = await apiClient.JSONGet<Product[]>("/api/product", {
				params: options?.catalogUpdate ? { catalog_update: true } : undefined,
			});
			products.value = page;
			return page;
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to load products";
			throw err;
		} finally {
			loading.value = false;
		}
	}

	async function get(id: number): Promise<Product> {
		error.value = null;
		try {
			return await apiClient.JSONGet<Product>(`/api/product/${id}`);
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to load product";
			throw err;
		}
	}

	// save writes one district product edit (the fields present in patch) and
	// returns after the server has applied it and reconciled the treatments that
	// follow it. It does not reload the list: an auto-save fires per field, so
	// the caller keeps the row it already holds rather than re-reading three
	// dozen products for every keystroke.
	async function save(id: number, patch: ProductPatch): Promise<void> {
		error.value = null;
		try {
			await apiClient.JSONPut(`/api/product/${id}`, patch);
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to save product";
			throw err;
		}
	}

	// createFromCatalog adds the shared catalog entry (its stable product_id
	// slug) to the district's own product list and reloads, so the new row
	// appears with the class and residual the server seeded from the catalog.
	// The catalog is never written; the district owns the row it creates. A
	// product the district retired is revived by the same call, with its
	// curation intact.
	async function createFromCatalog(productCatalogID: string): Promise<Product> {
		error.value = null;
		try {
			const created = await apiClient.JSONPost<Product>("/api/product", {
				product_catalog_id: productCatalogID,
			});
			await list();
			return created;
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to add product";
			throw err;
		}
	}

	// retire removes one district product from the district's list by retiring
	// it (the API never hard-deletes it; treatments still name it) and reloads,
	// so the list drops the row. The shared catalog entry is untouched.
	async function retire(id: number): Promise<void> {
		error.value = null;
		try {
			await apiClient.JSONDelete(`/api/product/${id}`);
			await list();
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to remove product";
			throw err;
		}
	}

	// catalogUpdatePreview is the district-side comparison of the linked catalog
	// entry against this row: what the catalog says now, what the district has,
	// and which fields the district itself changed. It is server-computed; the
	// client never derives it from names or dates.
	async function catalogUpdatePreview(
		id: number,
	): Promise<CatalogUpdatePreview> {
		return await apiClient.JSONGet<CatalogUpdatePreview>(
			`/api/product/${id}/catalog-update`,
		);
	}

	// acceptCatalogUpdate applies only the selected fields and records the
	// catalog revision the row is now on. Accepting some fields still catches
	// the row up for that revision.
	async function acceptCatalogUpdate(
		id: number,
		fields: string[],
	): Promise<CatalogUpdatePreview> {
		const preview = await apiClient.JSONPost<CatalogUpdatePreview>(
			`/api/product/${id}/catalog-update`,
			{ fields },
		);
		await list();
		return preview;
	}

	return {
		// State
		products,
		// Actions
		list,
		get,
		save,
		createFromCatalog,
		retire,
		catalogUpdatePreview,
		acceptCatalogUpdate,
		loading,
		error,
	};
});
